import {Separator} from 'platform/components';
import {getValueByDevice, GridItem, Heading, Space, useDevice, VStack} from 'platform/foundation';

import {featureFlags} from '@dms/feature-flags';
import {
  VehicleForm as VFKit,
  VehicleFieldGroups as VFGroup,
  VehicleFormDefaultLayout,
  useGetTenantInfo,
  isTrailerOrSemitrailer,
  isVanOrTruck,
  ConsumptionEmissionsSubSection,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {useNavigate, useRequiredParams} from 'shared/web';

import {AdditionalInfoWidget} from '../components/AdditionalInfoWidget';

export const BasicInformation = () => {
  const navigate = useNavigate();
  const {id: vehicleId} = useRequiredParams();
  const {isGermanTenant} = useGetTenantInfo();

  const device = useDevice();
  const isExpandable = getValueByDevice(device, true, true, true, false);

  const [canReadBusinessCase] = usePermissions({
    permissionKeys: ['businessCaseRead'],
  });

  return (
    <VehicleFormDefaultLayout>
      <VehicleFormDefaultLayout.Left>
        <VStack spacing={4}>
          {/* Basic Information */}
          <VFKit.Section
            data-testid="basic-information"
            isExpandable={isExpandable}
            title={isExpandable ? i18n.t('general.labels.basicInformation') : undefined}
          >
            <VFKit.VinDecoderField variant="sale" shouldRecommendVehicles hideChooseButton />
            <VFKit.BodyTypeChips />

            <VFKit.Group data-testid="make-model">
              <VFKit.MakeSelect />
              <VFKit.ModelSelect />
              <VFKit.ShowWhen
                name="type"
                condition={(type) =>
                  !isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
                }
              >
                <VFKit.SimpleTextField name="trim" />
              </VFKit.ShowWhen>
              <VFKit.SimpleTextField name="variant" />
            </VFKit.Group>

            <VFKit.Group data-testid="title" columns={isGermanTenant ? [1, 2, 6] : [1, 2, 4]}>
              <GridItem span={isGermanTenant ? [1, 2, 3] : [1, 2]}>
                <VFKit.TitleField />
              </GridItem>
              {isGermanTenant && (
                <VFKit.Suspense>
                  <VFGroup.GermanSpecific />
                </VFKit.Suspense>
              )}
            </VFKit.Group>

            <VFKit.Group data-testid="vehicle-registration-details" columns={[1, 2, 4]}>
              <VFGroup.VehicleRegistrationDetails />
            </VFKit.Group>

            <VFKit.ShowWhen name="type" condition={(type) => type?.at(0) !== 'VEHICLETYPE_OTHER'}>
              <VFKit.BodyStyleChips />
            </VFKit.ShowWhen>

            <VFKit.ShowWhen
              name="type"
              condition={(type) =>
                isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
              }
            >
              <VFKit.AxleCountChips />
            </VFKit.ShowWhen>

            <VFKit.ShowWhen
              name="type"
              condition={(type) =>
                !isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
              }
            >
              <VFGroup.Fuel />

              <VFKit.Group data-testid="transmission-drive" columns={[1, 2]}>
                <VFKit.TransmissionChips />
                <VFKit.DriveChips />
              </VFKit.Group>
              <VFKit.Group data-testid="engine-specs" columns={[1, 2, 4]}>
                <VFGroup.EngineSpecs />
              </VFKit.Group>

              <VFKit.Group data-testid="gear-count" columns={[1, 2, 4]}>
                <VFKit.SimpleNumericField name="gearCount" />
              </VFKit.Group>

              <Separator spacing={1} />

              <VFKit.Group data-testid="doors-seats" columns={[1, 2, 2]}>
                <VFKit.DoorTypesChips />
                <VFKit.SeatCountChips />
              </VFKit.Group>

              <VFKit.BodyStyleColorTypeChips />

              <VFKit.Group data-testid="exterior-color" columns={[1, 2, 4]}>
                <VFGroup.ExteriorColor />
              </VFKit.Group>

              <VFKit.InteriorMaterialChips />

              <VFKit.Group data-testid="interior-color" columns={[1, 2, 4]}>
                <VFKit.InteriorColorSelect />
                <VFKit.SimpleTextField name="interiorColorName" />
              </VFKit.Group>
            </VFKit.ShowWhen>
            <VFKit.HandDriveChips />

            <VFKit.ShowWhen
              whenFeatureEnabled={featureFlags.ADVERTISEMENT_PER_PLATFORM_ATTRIBUTES}
              name="type"
              condition={(type) =>
                (type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR') === 'VEHICLETYPE_PASSENGER_CAR'
              }
            >
              <VFKit.TruckHomologationCheckbox />
            </VFKit.ShowWhen>
          </VFKit.Section>

          {/* Additional Information */}
          <VFKit.Section
            title={i18n.t('general.labels.additionalInformation')}
            data-testid="additional-information"
            isExpandable={isExpandable}
          >
            <VFKit.ShowWhen
              name="type"
              condition={(type) =>
                !isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
              }
            >
              <Heading size={4}>{i18n.t('entity.vehicle.labels.engine')}</Heading>
              <VFKit.Group data-testid="engine-info" columns={[1, 2, 4]}>
                <VFGroup.EngineInfo />
              </VFKit.Group>
              <Separator spacing={2} />
              <ConsumptionEmissionsSubSection />
              <Separator spacing={2} />
            </VFKit.ShowWhen>

            <Heading size={4}>{i18n.t('entity.vehicle.labels.dimensions')}</Heading>
            <VFKit.ShowWhen
              name="type"
              condition={(type) => isVanOrTruck(type?.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')}
            >
              <VFKit.Group data-testid="dimensions" columns={[1, 2, 4]}>
                <VFKit.DimensionLengthSelect />
                <VFKit.DimensionsHeightOptions />
              </VFKit.Group>
            </VFKit.ShowWhen>
            <VFKit.Group data-testid="dimensions" columns={[1, 2, 4]}>
              <VFGroup.Dimensions />
              <VFKit.ShowWhen
                name="type"
                condition={(type) =>
                  isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
                }
              >
                <VFKit.SimpleNumericField name="europalletStorageSpaces" />
              </VFKit.ShowWhen>
              <VFKit.ShowWhen
                name="type"
                condition={(type) =>
                  !isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
                }
              >
                <div />
              </VFKit.ShowWhen>
              <VFKit.ShowWhen
                name="type"
                condition={(type) =>
                  isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR') ||
                  isVanOrTruck(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
                }
              >
                <VFGroup.LoadingSpaceDimensions />
              </VFKit.ShowWhen>
            </VFKit.Group>

            <VFKit.Group data-testid="weight" columns={[1, 2, 4]}>
              <VFGroup.Weight />
            </VFKit.Group>

            <VFKit.Group data-testid="trunk-wheelbase" columns={[1, 2, 4]}>
              <VFKit.ShowWhen
                name="type"
                condition={(type) =>
                  !isTrailerOrSemitrailer(type.at(0) ?? 'VEHICLETYPE_PASSENGER_CAR')
                }
              >
                <VFKit.SimpleNumericField name="trunkSpace" />
              </VFKit.ShowWhen>
              <VFKit.SimpleNumericField name="wheelBase" />
            </VFKit.Group>
          </VFKit.Section>
        </VStack>
      </VehicleFormDefaultLayout.Left>
      <VehicleFormDefaultLayout.Right>
        <VFKit.SubSection data-testid="additional-info">
          <AdditionalInfoWidget />
        </VFKit.SubSection>
        <Space vertical={4} />
        <VFKit.ShowWhen when={canReadBusinessCase} vehicleVariants={['sale']}>
          <VFKit.SubSection
            data-testid="cebia-widgets"
            title={i18n.t('entity.vehicle.labels.integrations')}
            isExpandable={isExpandable}
            useCard
            cardVariant={isExpandable ? 'default' : 'inlineWhite'}
            actions={[
              {
                variant: 'link',
                type: 'button',
                leftIcon: 'action/settings',
                onClick: () => navigate(settingsRoutes.vehicleServices),
                title: i18n.t('general.labels.settings'),
              },
            ]}
          >
            <VFKit.Suspense>
              <VFKit.CebiaAutotracerWidget vehicleId={vehicleId} />
              <VFKit.CebiaProductionYearWidget vehicleId={vehicleId} />
            </VFKit.Suspense>
          </VFKit.SubSection>
        </VFKit.ShowWhen>
      </VehicleFormDefaultLayout.Right>
    </VehicleFormDefaultLayout>
  );
};
